import { useState } from 'react';
import { PageLoader } from '@/components/ui/spinner';
import { useLocation } from 'wouter';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Star, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiCallError } from '@/lib/api';
import { useCurrentAdmin } from '@/hooks/use-current-admin';
import { AdminShell } from '@/components/admin-shell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription } from '@/components/ui/empty';

interface Plan {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  priceAmount: number;
  currency: string;
  billingPeriod: 'monthly' | 'yearly';
  features: string[];
  invoiceLimit: number | null;
  featureFlags: {
    marangatu_sync?: boolean;
    recommendations?: boolean;
    multi_ruc?: boolean;
  };
  featured: boolean;
  active: boolean;
  sortOrder: number;
}

function formatMoney(amount: number, currency: string): string {
  const prefix = currency === 'PYG' ? 'Gs' : currency;
  return `${prefix} ${Math.round(amount).toLocaleString('es-PY')}`;
}

export function PlansListPage() {
  const [, navigate] = useLocation();
  const { data: me, isLoading: loadingMe } = useCurrentAdmin();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Plan | 'new' | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Plan | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: () => api<{ items: Plan[] }>('/api/admin/plans'),
    enabled: !!me,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/api/admin/plans/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plans'] });
      toast.success('Plan eliminado');
    },
    onError: () => toast.error('No se pudo eliminar el plan'),
  });

  if (loadingMe) return <PageLoader />;
  if (!me) {
    navigate('/login');
    return null;
  }
  const isSuper = me.role === 'superadmin';

  return (
    <AdminShell>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Planes</h1>
          <p className="text-sm text-muted-foreground">
            Catálogo de planes comerciales. (No conectado al cobro todavía.)
          </p>
        </div>
        {isSuper && (
          <Button className="w-full sm:w-auto" onClick={() => setEditing('new')}>
            <Plus />
            Nuevo plan
          </Button>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-background shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Plan</TableHead>
              <TableHead className="text-right">Precio</TableHead>
              <TableHead>Período</TableHead>
              <TableHead className="text-right">Límite</TableHead>
              <TableHead>Estado</TableHead>
              {isSuper && <TableHead></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  Cargando…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && data?.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Empty>
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <CreditCard />
                      </EmptyMedia>
                      <EmptyTitle>No hay planes todavía</EmptyTitle>
                      <EmptyDescription>Creá el primer plan comercial.</EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </TableCell>
              </TableRow>
            )}
            {data?.items.map((p) => (
              <TableRow key={p.id}>
                <TableCell>
                  <div className="flex items-center gap-2 font-medium text-foreground">
                    {p.name}
                    {p.featured && (
                      <Star className="size-3.5 fill-primary text-primary" aria-label="Recomendado" />
                    )}
                  </div>
                  <div className="font-mono text-xs text-muted-foreground">{p.slug}</div>
                </TableCell>
                <TableCell className="text-right font-semibold tabular-nums whitespace-nowrap">
                  {formatMoney(p.priceAmount, p.currency)}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {p.billingPeriod === 'yearly' ? 'Anual' : 'Mensual'}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {p.invoiceLimit ?? 'Ilimitado'}
                </TableCell>
                <TableCell>
                  <Badge variant={p.active ? 'success' : 'secondary'}>
                    {p.active ? 'Activo' : 'Inactivo'}
                  </Badge>
                </TableCell>
                {isSuper && (
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(p)}>
                      <Pencil />
                      Editar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(p)}
                    >
                      <Trash2 />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {editing !== null && (
        <PlanFormDialog
          key={editing === 'new' ? 'new' : editing.id}
          plan={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar plan</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que querés eliminar <strong>{deleteTarget?.name}</strong>? Esta acción no se
              puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:opacity-90"
              onClick={() => {
                if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
                setDeleteTarget(null);
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}

function PlanFormDialog({ plan, onClose }: { plan: Plan | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(plan?.name ?? '');
  const [slug, setSlug] = useState(plan?.slug ?? '');
  const [description, setDescription] = useState(plan?.description ?? '');
  const [priceAmount, setPriceAmount] = useState(String(plan?.priceAmount ?? ''));
  const [currency, setCurrency] = useState(plan?.currency ?? 'PYG');
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>(
    plan?.billingPeriod ?? 'monthly'
  );
  const [features, setFeatures] = useState((plan?.features ?? []).join('\n'));
  const [invoiceLimit, setInvoiceLimit] = useState(
    plan?.invoiceLimit == null ? '' : String(plan.invoiceLimit)
  );
  const [featureFlags, setFeatureFlags] = useState(plan?.featureFlags ?? {});
  const [featured, setFeatured] = useState(plan?.featured ?? false);
  const [active, setActive] = useState(plan?.active ?? true);
  const [sortOrder, setSortOrder] = useState(String(plan?.sortOrder ?? 0));

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim(),
        slug: slug.trim(),
        description: description.trim() || null,
        priceAmount: Number(priceAmount) || 0,
        currency: currency.trim() || 'PYG',
        billingPeriod,
        features: features
          .split('\n')
          .map((f) => f.trim())
          .filter(Boolean),
        invoiceLimit: invoiceLimit.trim() === '' ? null : Number(invoiceLimit),
        featureFlags,
        featured,
        active,
        sortOrder: Number(sortOrder) || 0,
      };
      return plan
        ? api(`/api/admin/plans/${plan.id}`, { method: 'PATCH', body: JSON.stringify(payload) })
        : api('/api/admin/plans', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plans'] });
      toast.success(plan ? 'Plan actualizado' : 'Plan creado');
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiCallError && e.body.error === 'slug_already_exists') {
        toast.error('Ya existe un plan con ese slug');
      } else {
        toast.error('No se pudo guardar el plan');
      }
    },
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{plan ? 'Editar plan' : 'Nuevo plan'}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="p-name">Nombre</Label>
              <Input id="p-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="p-slug">Slug</Label>
              <Input
                id="p-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="basico"
                className="font-mono"
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="p-desc">Descripción</Label>
            <Input
              id="p-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Para quien solo declara IVA."
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="p-price">Precio</Label>
              <Input
                id="p-price"
                type="number"
                value={priceAmount}
                onChange={(e) => setPriceAmount(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="p-currency">Moneda</Label>
              <Input
                id="p-currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Período</Label>
              <Select value={billingPeriod} onValueChange={(v) => setBillingPeriod(v as 'monthly' | 'yearly')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Mensual</SelectItem>
                  <SelectItem value="yearly">Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="p-features">Características (una por línea)</Label>
            <Textarea
              id="p-features"
              rows={5}
              value={features}
              onChange={(e) => setFeatures(e.target.value)}
              placeholder={'IVA mensual\nIRP anual\nHasta 40 comprobantes'}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="p-limit">Límite de comprobantes</Label>
              <Input
                id="p-limit"
                type="number"
                value={invoiceLimit}
                onChange={(e) => setInvoiceLimit(e.target.value)}
                placeholder="vacío = ilimitado"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="p-order">Orden</Label>
              <Input
                id="p-order"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label>Módulos habilitados</Label>
            <div className="rounded-md border border-border p-3 space-y-2">
              {(
                [
                  { key: 'marangatu_sync', label: 'Sync con Marangatu (SET)' },
                  { key: 'recommendations', label: 'Recomendaciones financieras IA' },
                  { key: 'multi_ruc', label: 'Múltiples RUCs por tenant' },
                ] as const
              ).map(({ key, label }) => (
                <label key={key} className="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox
                    checked={!!featureFlags[key]}
                    onCheckedChange={(v) =>
                      setFeatureFlags((prev) => ({ ...prev, [key]: v === true }))
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox checked={featured} onCheckedChange={(v) => setFeatured(v === true)} />
              Recomendado
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox checked={active} onCheckedChange={(v) => setActive(v === true)} />
              Activo
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending || !name.trim() || !slug.trim()}
          >
            {save.isPending ? 'Guardando…' : plan ? 'Guardar cambios' : 'Crear plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
